import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Edit, Trash2, Clock, DollarSign, Check, FileText, Sparkles } from 'lucide-react';
import { 
  AddonsLibraryItem, 
  getAddonsLibraryItems, 
  createAddonsLibraryItem, 
  updateAddonsLibraryItem, 
  deleteAddonsLibraryItem 
} from '../lib/addonsLibrary';
import AIAddonCreatorModal from './AIAddonCreatorModal';
import { useBusiness } from '../contexts/BusinessContext';
import toast from 'react-hot-toast';

interface AddonsLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: 'manage' | 'select';
  serviceId?: string;
  onSelect?: (addons: AddonsLibraryItem[]) => void;
}

interface AddonsFormData {
  name: string;
  description: string;
  price: number;
  duration_minutes: number;
}

export default function AddonsLibraryModal({
  isOpen,
  onClose,
  mode = 'manage',
  serviceId,
  onSelect
}: AddonsLibraryModalProps) {
  const { selectedBusiness } = useBusiness();
  const [addons, setAddons] = useState<AddonsLibraryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAddon, setEditingAddon] = useState<AddonsLibraryItem | null>(null);
  const [selectedAddons, setSelectedAddons] = useState<string[]>([]);
  const [selectionOrder, setSelectionOrder] = useState<string[]>([]);
  const [currentMode, setCurrentMode] = useState<'manage' | 'select'>(mode);
  const [isAIAddonCreatorOpen, setIsAIAddonCreatorOpen] = useState(false);
  
  const [formData, setFormData] = useState<AddonsFormData>({
    name: '',
    description: '',
    price: 0,
    duration_minutes: 0
  });

  useEffect(() => {
    if (isOpen) {
      setCurrentMode(mode); // Sync with prop when modal opens
      loadAddons();
      setSelectedAddons([]); // Reset selection when modal opens
      setSelectionOrder([]); // Reset selection order when modal opens
    }
  }, [isOpen, mode]);

  const loadAddons = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      setError(null);
      const addonsData = await getAddonsLibraryItems(selectedBusiness.id);
      setAddons(addonsData);
    } catch (err) {
      console.error('Error loading addons library:', err);
      setError(err instanceof Error ? err.message : 'Failed to load addons library');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAddon = async () => {
    if (!selectedBusiness?.id) {
      toast.error('No business selected');
      return;
    }

    if (!formData.name.trim()) {
      toast.error('Add-on name is required');
      return;
    }

    try {
      setLoading(true);
      await createAddonsLibraryItem({
        business_id: selectedBusiness.id,
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        price: formData.price,
        duration_minutes: formData.duration_minutes
      });

      toast.success('Add-on created successfully');
      setShowCreateForm(false);
      resetForm();
      loadAddons();
    } catch (err) {
      console.error('Error creating addon:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to create add-on');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAddon = async () => {
    if (!editingAddon) return;

    if (!formData.name.trim()) {
      toast.error('Add-on name is required');
      return;
    }

    try {
      setLoading(true);
      await updateAddonsLibraryItem(editingAddon.id, {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        price: formData.price,
        duration_minutes: formData.duration_minutes
      });

      toast.success('Add-on updated successfully');
      setEditingAddon(null);
      setShowCreateForm(false);
      resetForm();
      loadAddons();
    } catch (err) {
      console.error('Error updating addon:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to update add-on');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddon = async (addonId: string) => {
    if (!window.confirm('Are you sure you want to delete this add-on?')) {
      return;
    }

    try {
      setLoading(true);
      await deleteAddonsLibraryItem(addonId);
      toast.success('Add-on deleted successfully');
      loadAddons();
    } catch (err) {
      console.error('Error deleting addon:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to delete add-on');
    } finally {
      setLoading(false);
    }
  };

  const handleEditAddon = (addon: AddonsLibraryItem) => {
    setEditingAddon(addon);
    setFormData({
      name: addon.name,
      description: addon.description || '',
      price: addon.price,
      duration_minutes: addon.duration_minutes
    });
    setShowCreateForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: 0,
      duration_minutes: 0
    });
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingAddon(null);
    resetForm();
  };

  const toggleAddonSelection = (addonId: string) => {
    const isCurrentlySelected = selectedAddons.includes(addonId);
    
    if (isCurrentlySelected) {
      // Remove from selection
      setSelectedAddons(prev => prev.filter(id => id !== addonId));
      setSelectionOrder(prev => prev.filter(id => id !== addonId));
    } else {
      // Add to selection
      setSelectedAddons(prev => [...prev, addonId]);
      setSelectionOrder(prev => [...prev, addonId]);
    }
    
    // Debug logging
    console.log('Toggling addon:', addonId);
    console.log('Currently selected:', selectedAddons);
    console.log('Selection order:', selectionOrder);
  };

  const handleAddToService = () => {
    // Return add-ons in the order they were selected
    const selectedAddonItems = selectionOrder
      .map(id => addons.find(addon => addon.id === id))
      .filter(addon => addon !== undefined) as AddonsLibraryItem[];
    onSelect?.(selectedAddonItems);
  };

  const formatDuration = (minutes: number) => {
    if (minutes === 0) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  if (!isOpen) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <div 
      className="fixed inset-0 transition-opacity duration-200 bg-black/50"
      style={{
        zIndex: 100400,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
      onClick={onClose}
    >
      <div 
        className={`bg-white shadow-2xl flex flex-col overflow-hidden transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[700px] lg:w-[800px] ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100450
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
          {currentMode === 'select' && (
            <button
              onClick={() => setCurrentMode('manage')}
              className="absolute top-4 left-4 inline-flex items-center px-3 py-2 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 active:bg-blue-200 transition-all duration-150 active:scale-[0.98] touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <Plus className="h-3 w-3 mr-1" />
              Create Addon
            </button>
          )}
          
          {/* Create with AI Button */}
          {currentMode === 'manage' && (
            <button
              type="button"
              onClick={() => setIsAIAddonCreatorOpen(true)}
              className="absolute right-6 top-1/2 -translate-y-1/2 flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 active:scale-95 transition-all duration-150 shadow-lg hover:shadow-xl"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <Sparkles className="h-4 w-4" />
              <span className="text-sm font-semibold">Create with AI</span>
            </button>
          )}
          
          <h2 className="text-2xl font-bold text-gray-900" style={{ 
            letterSpacing: '-0.02em'
          }}>
            {currentMode === 'select' ? 'Select Add-on' : 'Add-ons Library'}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {currentMode === 'select' 
              ? 'Choose an add-on template to add to your service'
              : 'Manage your reusable add-on templates'
            }
          </p>
        </div>

        {/* Content */}
        <div 
          className="flex-1 overflow-y-auto bg-white"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`}>
            {error && (
              <div className="bg-red-50 border border-red-200/60 text-red-700 px-4 py-3 rounded-xl text-sm shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                {error}
              </div>
            )}

            {/* Mode Switch Button - Show in manage mode to go back to select */}
            {currentMode === 'manage' && serviceId && onSelect && (
              <div className="flex justify-end">
                <button
                  onClick={() => setCurrentMode('select')}
                  className="inline-flex items-center px-3 py-2 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 active:bg-blue-200 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Check className="h-3 w-3 mr-1" />
                  Select Mode
                </button>
              </div>
            )}

            {/* Create/Edit Form */}
            {showCreateForm && (
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <h3 className="text-base font-semibold text-gray-900 mb-4">
                  {editingAddon ? 'Edit Add-on' : 'Create New Add-on'}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                      style={{ fontSize: '16px' }}
                      placeholder="Enter add-on name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Price ($)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                      style={{ fontSize: '16px' }}
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Duration (minutes)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.duration_minutes}
                      onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 0 }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                      style={{ fontSize: '16px' }}
                      placeholder="0"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 resize-none"
                      style={{ fontSize: '16px' }}
                      placeholder="Optional description"
                    />
                  </div>

                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={handleCancel}
                    className="px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 active:bg-gray-300 active:scale-[0.98] transition-all duration-150 touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={editingAddon ? handleUpdateAddon : handleCreateAddon}
                    disabled={loading}
                    className="px-4 py-3 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.98] touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2 inline-block"></div>
                        Saving...
                      </>
                    ) : (
                      editingAddon ? 'Update' : 'Create'
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Add New Button */}
            {!showCreateForm && currentMode === 'manage' && (
              <div>
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="inline-flex items-center px-4 py-2 text-sm font-semibold text-blue-600 bg-blue-50 rounded-xl hover:bg-blue-100 active:bg-blue-200 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add New Add-on
                </button>
              </div>
            )}

            {/* Loading State */}
            {loading && !showCreateForm && (
              <div className="py-12 text-center">
                <div className="inline-flex items-center px-4 py-2 font-semibold text-sm text-gray-500 rounded-xl bg-gray-100">
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin mr-3"></div>
                  Loading add-ons...
                </div>
              </div>
            )}

            {/* Add-ons List */}
            {!loading && !showCreateForm && (
              <div>
                {addons.length === 0 ? (
                  <div className="bg-white rounded-[12px] border border-gray-200/60 p-8 text-center shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                    <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No add-ons yet</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      {currentMode === 'select' 
                        ? 'No add-on templates available. Create some templates first.'
                        : 'Create your first add-on template to get started.'
                      }
                    </p>
                    {currentMode === 'manage' && (
                      <button
                        onClick={() => setShowCreateForm(true)}
                        className="inline-flex items-center px-4 py-2 text-sm font-semibold text-blue-600 bg-blue-50 rounded-xl hover:bg-blue-100 active:bg-blue-200 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create Add-on
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {addons.map((addon) => (
                      <div
                        key={addon.id}
                        className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)] active:scale-[0.98] transition-all duration-150"
                        style={{ touchAction: 'manipulation' }}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-semibold text-gray-900 flex-1">{addon.name}</h4>
                          {currentMode === 'select' ? (
                            <div className="flex items-center space-x-2">
                              {selectedAddons.includes(addon.id) && (
                                <span className="text-xs font-bold text-blue-600 bg-blue-100 rounded-full w-5 h-5 flex items-center justify-center">
                                  {selectionOrder.indexOf(addon.id) + 1}
                                </span>
                              )}
                              <button
                                onClick={() => handleEditAddon(addon)}
                                title="Edit add-on (tax, price, etc.)"
                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-150 active:scale-[0.95] touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => toggleAddonSelection(addon.id)}
                                className={`p-2 rounded-xl transition-all duration-150 active:scale-[0.95] touch-manipulation ${
                                  selectedAddons.includes(addon.id)
                                    ? 'text-blue-600 bg-blue-100'
                                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                                }`}
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                <Check className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex space-x-1">
                              <button
                                onClick={() => handleEditAddon(addon)}
                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-150 active:scale-[0.95] touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteAddon(addon.id)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all duration-150 active:scale-[0.95] touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                        </div>
                        
                        {addon.description && (
                          <p className="text-sm text-gray-600 mb-3">{addon.description}</p>
                        )}
                        
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center text-green-600 font-semibold">
                            <DollarSign className="h-4 w-4 mr-1" />
                            ${addon.price.toFixed(2)}
                          </div>
                          <div className="flex items-center text-blue-600 font-semibold">
                            <Clock className="h-4 w-4 mr-1" />
                            {formatDuration(addon.duration_minutes)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer for Select Mode */}
        {currentMode === 'select' && !showCreateForm && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
            <div className="text-sm font-semibold text-gray-600">
              {selectedAddons.length} add-on{selectedAddons.length !== 1 ? 's' : ''} selected
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 active:bg-gray-300 active:scale-[0.98] transition-all duration-150 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddToService}
                disabled={selectedAddons.length === 0}
                className="px-4 py-3 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.98] touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Add to Service
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* AI Addon Creator Modal */}
      <AIAddonCreatorModal
        isOpen={isAIAddonCreatorOpen}
        onClose={() => setIsAIAddonCreatorOpen(false)}
        onAddonsCreated={async (addons) => {
          console.log('📦 Add-ons created:', addons.length);
          // Reload addons library to show newly created add-ons
          await loadAddons();
          // Keep the AddonsLibraryModal open so user can apply them immediately
        }}
        businessId={selectedBusiness?.id || ''}
      />
    </div>
  );

  return createPortal(modalContent, document.body);
}
